import { useQuery } from "@apollo/client";
import { compact, uniqBy } from "lodash";
import { useEffect, useMemo, useState } from "react";
import {
  AssessmentGroupProviders,
  AssessmentType,
  ComplianceProgramParticipantStatus,
  GetComplianceProgramProgressQuery,
  GetComplianceProgramProgressQueryVariables,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import {
  currentOrganizationVar,
  currentUserVar,
  isAdminVar,
} from "@cache/cache";
import Layout from "@components/layout/layout";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import useDocumentTitle from "@components/use-document-title/use-document-title";
import useUserComboboxQuery from "@components/user-combobox/use-user-combobox-query";
import UserCombobox from "@components/user-combobox/user-combobox";
import {
  UserComboboxDirectReportsOption,
  UserComboboxOption,
  UserComboboxOptionType,
  UserComboboxOrgOption,
  UserComboboxReportingTreeOption,
  UserComboboxTeamOption,
} from "@components/user-combobox/user-combobox-list";
import { getAssessmentTypeLabel } from "@helpers/constants";
import { classNames } from "@helpers/css";
import {
  assertEdgesNonNull,
  assertEdgesNonNullWithStringId,
} from "@helpers/helpers";
import useUrlQueryParams from "@helpers/hooks/use-url-query-params";

import getComplianceProgramProgressQuery from "../graphql/get-compliance-program-progress-query";
import { filterParticipantsToBeReminded } from "./compliance-program-progress-constants";
import ComplianceProgramProgressEntityTable, {
  ComplianceProgramReportingEntityTableCol,
} from "./compliance-program-progress-entity-table";
import ComplianceProgramProgressUserTable from "./compliance-program-progress-user-table";

function typeGuardIsUserComboboxTeamOption(
  obj: UserComboboxOption,
): obj is UserComboboxTeamOption {
  return obj.type === UserComboboxOptionType.TEAM;
}

const defaultDirectReportsOption: UserComboboxDirectReportsOption = {
  type: UserComboboxOptionType.DIRECT_REPORTS,
  id: UserComboboxOptionType.DIRECT_REPORTS,
};

const ComplianceProgramProgress = ({
  complianceProgramId,
}: {
  complianceProgramId: number;
}) => {
  const link = useLink();
  const label = useLabel();
  const url = `/programs/${complianceProgramId}/progress`;
  const currentOrganization = currentOrganizationVar();
  const currentUser = currentUserVar();
  const directReportIds = assertEdgesNonNull(currentUser.directReports).map(
    ({ id }) => id,
  );
  const reportingTreeIds = compact(currentUser.reportingTreeIds).filter(
    (id) => !directReportIds.includes(id),
  );
  const isAdmin = isAdminVar();
  const isManager = directReportIds.length > 0;
  const hasReportingTree = reportingTreeIds.length > 0;
  const urlParams = useUrlQueryParams<{
    team: string | null;
    [UserComboboxOptionType.DIRECT_REPORTS]: string | null;
    [UserComboboxOptionType.REPORTING_TREE]: string | null;
  }>({
    team: null,
    [UserComboboxOptionType.DIRECT_REPORTS]: null,
    [UserComboboxOptionType.REPORTING_TREE]: null,
  });
  const teamUrlParam = urlParams.team ? parseInt(urlParams.team) : null;

  const defaultOrgContext: UserComboboxOrgOption = useMemo(
    () => ({
      id: currentOrganization.id,
      name: currentOrganization.name,
      type: UserComboboxOptionType.ORG,
    }),
    [currentOrganization.id, currentOrganization.name],
  );

  const [context, setContext] = useState<
    | UserComboboxTeamOption
    | UserComboboxOrgOption
    | UserComboboxDirectReportsOption
    | UserComboboxReportingTreeOption
    | null
  >(null);

  const {
    options,
    query,
    setQuery,
    loading: isLoadingUsers,
  } = useUserComboboxQuery({
    types: compact([
      isAdmin && UserComboboxOptionType.ORG,
      isAdmin && UserComboboxOptionType.TEAM,
      isManager && UserComboboxOptionType.DIRECT_REPORTS,
      hasReportingTree && UserComboboxOptionType.REPORTING_TREE,
    ]),
    queryOptions: { skip: !complianceProgramId },
    selected: context,
  });

  const optionMatchingUrlParam = teamUrlParam
    ? isAdmin &&
      options
        .filter(typeGuardIsUserComboboxTeamOption)
        .find((option) => option.id === teamUrlParam)
    : urlParams.directReports
      ? options.find(
          (opt) => opt.type === UserComboboxOptionType.DIRECT_REPORTS,
        )
      : urlParams.reportingTree
        ? options.find(
            (opt) => opt.type === UserComboboxOptionType.REPORTING_TREE,
          )
        : isAdmin
          ? defaultOrgContext
          : defaultDirectReportsOption;

  useEffect(() => {
    if (
      optionMatchingUrlParam &&
      (context?.id !== optionMatchingUrlParam.id ||
        context?.type !== optionMatchingUrlParam.type)
    ) {
      setContext(optionMatchingUrlParam);
    }
  }, [context?.id, context?.type, optionMatchingUrlParam]);

  const handleChangeContext = (value: UserComboboxOption) => {
    if (
      value.type === UserComboboxOptionType.ORG ||
      value.type === UserComboboxOptionType.TEAM ||
      value.type === UserComboboxOptionType.DIRECT_REPORTS ||
      value.type === UserComboboxOptionType.REPORTING_TREE
    ) {
      link.redirect(`${url}?${value.type}=${value.id}`);
    }
  };

  const handleClearContext = () => {
    link.redirect(
      isAdmin
        ? `${url}?${defaultOrgContext.type}=${defaultOrgContext.id}`
        : `${url}`,
    );
  };

  const { data: complianceProgramData, loading: isLoadingComplianceProgram } =
    useQuery<
      GetComplianceProgramProgressQuery,
      GetComplianceProgramProgressQueryVariables
    >(getComplianceProgramProgressQuery, {
      fetchPolicy: "network-only",
      variables: { complianceProgramId },
    });

  const complianceProgram = useMemo(
    () => complianceProgramData?.complianceProgram,
    [complianceProgramData],
  );
  const performanceAssessmentTemplate = useMemo(
    () =>
      complianceProgram?.performanceAssessmentTemplate
        ? complianceProgram.performanceAssessmentTemplate
        : null,
    [complianceProgram],
  );
  const managerAssessmentTemplate = useMemo(
    () =>
      complianceProgram?.managerAssessmentTemplate
        ? complianceProgram.managerAssessmentTemplate
        : null,
    [complianceProgram],
  );
  const peerAssessmentTemplate = useMemo(
    () =>
      complianceProgram?.peerAssessmentTemplate
        ? complianceProgram.peerAssessmentTemplate
        : null,
    [complianceProgram],
  );
  const engagementSurveyTemplate = useMemo(
    () =>
      complianceProgram?.engagementSurveyTemplate
        ? complianceProgram.engagementSurveyTemplate
        : null,
    [complianceProgram],
  );
  const topicTemplate = useMemo(
    () =>
      complianceProgram?.requiredTopicTemplates
        ? assertEdgesNonNull(complianceProgram.requiredTopicTemplates)[0]
        : null,
    [complianceProgram],
  );

  const assessmentsOpenForNominations = useMemo(() => {
    return complianceProgram?.assessmentsOpenForNominations
      ? assertEdgesNonNullWithStringId(
          complianceProgram.assessmentsOpenForNominations,
        )
      : [];
  }, [complianceProgram]);

  const nominationCountByUserId = useMemo(() => {
    return assessmentsOpenForNominations.reduce(
      (memo, assessmentOpenForNominations) => {
        const userId = assessmentOpenForNominations.targetUser.id;
        memo[userId] = assessmentOpenForNominations.nominations.totalCount;
        return memo;
      },
      {} as Record<number, number>,
    );
  }, [assessmentsOpenForNominations]);

  const participantStatuses = useMemo(() => {
    return compact(
      complianceProgram?.participantStatus?.edges.map((edge) => edge?.node),
    ).map((participantStatus) => {
      // Nominations
      const nominations =
        nominationCountByUserId[participantStatus.user.id] > 0
          ? ComplianceProgramParticipantStatus.Complete
          : ComplianceProgramParticipantStatus.NotStarted;

      return {
        ...participantStatus,
        nominations,
      };
    });
  }, [complianceProgram?.participantStatus?.edges, nominationCountByUserId]);

  const showCalibrationColumn = useMemo(() => {
    return participantStatuses.some((participantStatus) => {
      return (
        participantStatus.calibrationStatus !==
        ComplianceProgramParticipantStatus.NotApplicable
      );
    });
  }, [participantStatuses]);
  const showSharingColumn = useMemo(() => {
    return participantStatuses.some((participantStatus) => {
      return (
        participantStatus.deliveryStatus !==
        ComplianceProgramParticipantStatus.NotApplicable
      );
    });
  }, [participantStatuses]);

  const flattenedParticipantStatuses = useMemo(() => {
    return participantStatuses.map((participantStatus) => {
      const flattenedTeams: UserComboboxTeamOption[] = assertEdgesNonNull(
        participantStatus.user.teams,
      ).map((node) => ({ ...node, type: UserComboboxOptionType.TEAM }));
      return {
        ...participantStatus,
        user: {
          ...participantStatus.user,
          flattenedTeams: flattenedTeams,
          teamIds: flattenedTeams.map((team) => team.id),
          manager: participantStatus.user.managersList[0],
        },
      };
    });
  }, [participantStatuses]);

  const teams = useMemo(() => {
    const concatenatedTeams = flattenedParticipantStatuses.reduce(
      (memo, participantStatus) => {
        return [...memo, ...participantStatus.user.flattenedTeams];
      },
      [] as UserComboboxTeamOption[],
    );
    return uniqBy(concatenatedTeams, "id");
  }, [flattenedParticipantStatuses]);

  const participantStatusesWithMatchingTeam = useMemo(() => {
    return flattenedParticipantStatuses.filter(
      (participantStatus) =>
        (context?.type === UserComboboxOptionType.TEAM &&
          participantStatus.user.teamIds.includes(context.id)) ||
        (context?.type === UserComboboxOptionType.DIRECT_REPORTS &&
          directReportIds.includes(participantStatus.user.id)) ||
        (context?.type === UserComboboxOptionType.REPORTING_TREE &&
          reportingTreeIds.includes(participantStatus.user.id)),
    );
  }, [
    flattenedParticipantStatuses,
    context,
    directReportIds,
    reportingTreeIds,
  ]);

  const contextWithParticipantStatuses = useMemo(() => {
    return context
      ? {
          ...context,
          participantStatuses:
            context?.type === UserComboboxOptionType.TEAM ||
            context.type === UserComboboxOptionType.DIRECT_REPORTS ||
            context.type === UserComboboxOptionType.REPORTING_TREE
              ? participantStatusesWithMatchingTeam
              : participantStatuses,
        }
      : null;
  }, [context, participantStatusesWithMatchingTeam, participantStatuses]);

  const teamsWithParticipantStatuses = useMemo(() => {
    return teams.map((team) => ({
      ...team,
      participantStatuses: flattenedParticipantStatuses.filter(
        (participantStatus) => participantStatus.user.teamIds.includes(team.id),
      ),
    }));
  }, [teams, flattenedParticipantStatuses]);

  const directReportsWithStatuses = useMemo(() => {
    return flattenedParticipantStatuses.filter((participantStatus) =>
      directReportIds.includes(participantStatus.user.id),
    );
  }, [flattenedParticipantStatuses, directReportIds]);

  const reportingTreeWithStatuses = useMemo(() => {
    return flattenedParticipantStatuses.filter((participantStatus) =>
      reportingTreeIds.includes(participantStatus.user.id),
    );
  }, [flattenedParticipantStatuses, reportingTreeIds]);

  const myReportingGroupEntities = useMemo(() => {
    const directReportsOption: UserComboboxDirectReportsOption = {
      type: UserComboboxOptionType.DIRECT_REPORTS,
      id: UserComboboxOptionType.DIRECT_REPORTS,
    };
    const reportingTreeOption: UserComboboxReportingTreeOption = {
      type: UserComboboxOptionType.REPORTING_TREE,
      id: UserComboboxOptionType.REPORTING_TREE,
    };
    return compact([
      isManager && {
        ...directReportsOption,
        participantStatuses: directReportsWithStatuses,
      },
      hasReportingTree && {
        ...reportingTreeOption,
        participantStatuses: reportingTreeWithStatuses,
      },
    ]);
  }, [
    isManager,
    hasReportingTree,
    directReportsWithStatuses,
    reportingTreeWithStatuses,
  ]);

  const cols: ComplianceProgramReportingEntityTableCol[] = useMemo(() => {
    const nominationRemindUsers =
      assessmentsOpenForNominations.length > 0
        ? assessmentsOpenForNominations
            .filter(
              (assessmentsOpenForNomination) =>
                assessmentsOpenForNomination.nominations.totalCount === 0,
            )
            .map(
              (assessmentsOpenForNomination) =>
                assessmentsOpenForNomination.targetUser,
            )
        : [];
    return compact([
      peerAssessmentTemplate &&
        assessmentsOpenForNominations.length > 0 && {
          label: `${getAssessmentTypeLabel(AssessmentType.Peer)} Nominations`,
          keyName: "nominations",
          remindUsers: nominationRemindUsers,
          remindUserType: AssessmentGroupProviders.ManagerSelect
            ? "manager"
            : "subject",
          remindUserTooltip:
            peerAssessmentTemplate.providers ===
            AssessmentGroupProviders.ManagerSelect
              ? `Managers of the peer ${label(
                  "review",
                )} subjects will be reminded to nominate peers.`
              : peerAssessmentTemplate.providers ===
                  AssessmentGroupProviders.SubjectSelect
                ? `Subjects will be reminded to nominate peers for their peer ${label(
                    "review",
                  )}.`
                : "",
        },
      performanceAssessmentTemplate &&
        performanceAssessmentTemplate.hasSelfAssessment && {
          label: `Self ${label("review", {
            capitalize: true,
          })}`,
          keyName: "performanceSelfAssessmentStatus",
          remindUserType: "subject",
          remindUsers: filterParticipantsToBeReminded(
            participantStatuses,
            "performanceSelfAssessmentStatus",
          ),
        },
      performanceAssessmentTemplate && {
        label: `Manager ${label("review", { capitalize: true })}`,
        keyName: "performanceAssessmentStatus",
        remindUserType: "manager",
        remindUsers: filterParticipantsToBeReminded(
          participantStatuses,
          "performanceAssessmentStatus",
        ),
      },
      engagementSurveyTemplate && {
        label: `Survey`,
        keyName: "engagementSurveyStatus",
        remindUserType: "survey_participant",
        remindUsers: filterParticipantsToBeReminded(
          participantStatuses,
          "engagementSurveyStatus",
        ),
      },
      peerAssessmentTemplate && {
        label: `${getAssessmentTypeLabel(AssessmentType.Peer)} ${label(
          "review",
          {
            capitalize: true,
          },
        )}`,
        keyName: "peerAssessmentStatus",
        remindUserType: "peer",
        remindUsers: filterParticipantsToBeReminded(
          participantStatuses,
          "peerAssessmentStatus",
        ),
      },
      managerAssessmentTemplate && {
        label: `${getAssessmentTypeLabel(AssessmentType.Manager)} ${label(
          "review",
          { capitalize: true },
        )}`,
        keyName: "managerAssessmentStatus",
        remindUserType: "directReport",
        remindUsers: filterParticipantsToBeReminded(
          participantStatuses,
          "managerAssessmentStatus",
        ),
      },
      showCalibrationColumn && {
        label: "Calibration",
        keyName: "calibrationStatus",
      },
      showSharingColumn && {
        label: "Share with Subject",
        keyName: "deliveryStatus",
      },
      topicTemplate && {
        label: "1-on-1 Meetings",
        keyName: "oneononeStatus",
        remindUserType: "manager",
        remindUsers: filterParticipantsToBeReminded(
          participantStatuses,
          "oneononeStatus",
        ),
      },
    ]);
  }, [
    peerAssessmentTemplate,
    engagementSurveyTemplate,
    assessmentsOpenForNominations,
    performanceAssessmentTemplate,
    participantStatuses,
    managerAssessmentTemplate,
    topicTemplate,
    showCalibrationColumn,
    showSharingColumn,
    label,
  ]);

  useDocumentTitle(complianceProgram?.title || "Program Reporting");

  return isLoadingComplianceProgram || !complianceProgram ? (
    <Loading />
  ) : (
    <>
      <div className="empty:hidden">
        {(isAdmin || isManager) && (
          <div
            className={classNames(
              "flex items-center gap-4 justify-between mb-6",
            )}
          >
            <div className="flex items-center gap-4">
              <UserCombobox
                disabled={isLoadingComplianceProgram}
                className="w-64"
                query={query}
                options={options}
                value={context}
                onChangeValue={handleChangeContext}
                onChangeQuery={setQuery}
                loading={isLoadingUsers}
                placeholder="Select..."
                searchPlaceholder="Search..."
                clearable={
                  context !== null &&
                  context.type !== UserComboboxOptionType.ORG &&
                  (context.type !== UserComboboxOptionType.DIRECT_REPORTS ||
                    isAdmin)
                }
                onClearValue={handleClearContext}
              />
              {isLoadingUsers && <Loading mini size={5} />}
            </div>
          </div>
        )}

        {contextWithParticipantStatuses && (
          <Layout.MainSubSection>
            <ComplianceProgramProgressEntityTable
              complianceProgram={complianceProgram}
              entities={[contextWithParticipantStatuses]}
              cols={cols}
            />
          </Layout.MainSubSection>
        )}
      </div>

      {(context?.type === UserComboboxOptionType.ORG || context === null) &&
        isAdmin && (
          <>
            <Layout.MainSubSection title="Departments">
              <ComplianceProgramProgressEntityTable
                complianceProgram={complianceProgram}
                entities={teamsWithParticipantStatuses}
                cols={cols}
              />
            </Layout.MainSubSection>

            {myReportingGroupEntities.length > 0 && (
              <Layout.MainSubSection title="My Reporting Groups">
                <ComplianceProgramProgressEntityTable
                  complianceProgram={complianceProgram}
                  entities={myReportingGroupEntities}
                  cols={cols}
                />
              </Layout.MainSubSection>
            )}

            <ComplianceProgramProgressUserTable
              title="Employees"
              participantStatuses={participantStatuses}
              cols={cols}
              usersLabel="members"
              complianceProgram={complianceProgram}
            />
          </>
        )}

      {isAdmin && context?.type === UserComboboxOptionType.TEAM && (
        <ComplianceProgramProgressUserTable
          title={`Status of ${label("team")} members`}
          usersLabel={`${label("team")} members`}
          participantStatuses={participantStatusesWithMatchingTeam}
          cols={cols}
          complianceProgram={complianceProgram}
        />
      )}
      {(context?.type === UserComboboxOptionType.REPORTING_TREE ||
        context?.type === UserComboboxOptionType.DIRECT_REPORTS) && (
        <ComplianceProgramProgressUserTable
          title="Status of Reports"
          usersLabel="reports"
          participantStatuses={participantStatusesWithMatchingTeam}
          cols={cols}
          complianceProgram={complianceProgram}
        />
      )}
    </>
  );
};

export default ComplianceProgramProgress;
